import { useState, useEffect } from 'react'
import { networkClient } from '../network/client'

interface SequencePuzzleData {
  puzzleId: string
  type: 'sequence'
  difficulty: string
  title: string
  description: string
  blocks: string[]
}

interface SequencePuzzlePanelProps {
  puzzle: SequencePuzzleData
  onClose: () => void
}

export default function SequencePuzzlePanel({ puzzle, onClose }: SequencePuzzlePanelProps) {
  const [order, setOrder] = useState<number[]>([])
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null)
  const [feedback, setFeedback] = useState<string | null>(null)
  const [hint, setHint] = useState<string | null>(null)
  const [hintsUsed, setHintsUsed] = useState(0)
  const [solved, setSolved] = useState(false)
  const [solvedData, setSolvedData] = useState<{ points: number; message: string; correctOrder: number[]; stabilityRestored?: number } | null>(null)

  // Initialize order with original indices
  useEffect(() => {
    setOrder(puzzle.blocks.map((_, i) => i))
  }, [puzzle.blocks])

  useEffect(() => {
    const room = networkClient.getGameRoom()
    if (!room) return

    const handleWrong = (data: { message: string }) => {
      setFeedback(data.message)
      setTimeout(() => setFeedback(null), 3000)
    }

    const handleHint = (data: { hint: string; hintsUsed: number }) => {
      setHint(data.hint)
      setHintsUsed(data.hintsUsed)
    }

    const handleSolved = (data: any) => {
      if (data.puzzleId === puzzle.puzzleId && data.type === 'sequence') {
        setSolved(true)
        setSolvedData(data)
      }
    }

    room.onMessage('puzzle_wrong', handleWrong)
    room.onMessage('puzzle_hint', handleHint)
    room.onMessage('puzzle_solved', handleSolved)

    return () => {}
  }, [puzzle.puzzleId])

  const handleDragStart = (idx: number) => {
    setDraggedIdx(idx)
  }

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault()
    if (draggedIdx === null || draggedIdx === idx) return

    const newOrder = [...order]
    const draggedItem = newOrder[draggedIdx]
    newOrder.splice(draggedIdx, 1)
    newOrder.splice(idx, 0, draggedItem)
    setOrder(newOrder)
    setDraggedIdx(idx)
  }

  const handleDragEnd = () => {
    setDraggedIdx(null)
  }

  const handleSubmit = () => {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('submit_sequence_answer', {
        puzzleId: puzzle.puzzleId,
        order: order,
      })
    }
  }

  const requestHint = () => {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('request_hint', { puzzleId: puzzle.puzzleId })
    }
  }

  const getDifficultyColor = (diff: string) => {
    switch (diff) {
      case 'easy': return 'text-green-400'
      case 'medium': return 'text-yellow-400'
      case 'hard': return 'text-red-400'
      default: return 'text-white'
    }
  }

  const getDifficultyLabel = (diff: string) => {
    switch (diff) {
      case 'easy': return 'Latwa'
      case 'medium': return 'Srednia'
      case 'hard': return 'Trudna'
      default: return diff
    }
  }

  if (solved && solvedData) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="bg-gray-800 border-2 border-emerald-neon rounded-xl p-8 max-w-lg text-center">
          <h2 className="text-3xl font-bold text-emerald-neon mb-4">Sekwencja poprawna!</h2>
          <p className="text-xl text-white mb-4">{solvedData.message}</p>
          <div className="text-4xl font-bold text-yellow-400 mb-2">+{solvedData.points} pkt</div>
          {solvedData.stabilityRestored && (
            <div className="text-lg text-emerald-neon mb-4">+{solvedData.stabilityRestored}% stabilnosci</div>
          )}

          <div className="mb-6 p-4 bg-gray-900 rounded-lg">
            <p className="text-gray-400 mb-2">Poprawna kolejnosc:</p>
            <div className="space-y-2">
              {solvedData.correctOrder.map((blockIdx, pos) => (
                <div
                  key={pos}
                  className="flex items-center gap-2 bg-emerald-neon/20 rounded-lg p-2"
                >
                  <span className="w-6 h-6 bg-emerald-neon text-gray-900 rounded-full flex items-center justify-center font-bold text-sm">
                    {pos + 1}
                  </span>
                  <span className="text-white">{puzzle.blocks[blockIdx]}</span>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={onClose}
            className="px-8 py-3 bg-emerald-neon text-gray-900 font-bold rounded-lg hover:bg-emerald-400"
          >
            Kontynuuj
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-gray-600 rounded-xl p-6 max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">{puzzle.title}</h2>
            <p className={`text-sm ${getDifficultyColor(puzzle.difficulty)}`}>
              Trudnosc: {getDifficultyLabel(puzzle.difficulty)}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        {/* Instructions */}
        <p className="text-gray-300 mb-6">{puzzle.description}</p>
        <p className="text-gray-400 text-sm mb-4">Przeciagnij bloki, aby ulozyc je w poprawnej kolejnosci.</p>

        {/* Sortable blocks */}
        <div className="space-y-2 mb-6">
          {order.map((blockIdx, displayIdx) => (
            <div
              key={blockIdx}
              draggable
              onDragStart={() => handleDragStart(displayIdx)}
              onDragOver={(e) => handleDragOver(e, displayIdx)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-move transition-all
                ${draggedIdx === displayIdx
                  ? 'bg-emerald-neon/30 border-2 border-emerald-neon scale-105'
                  : 'bg-gray-700 border-2 border-transparent hover:border-gray-500'
                }`}
            >
              <span className="w-8 h-8 bg-gray-600 text-white rounded-full flex items-center justify-center font-bold">
                {displayIdx + 1}
              </span>
              <span className="text-white flex-1">{puzzle.blocks[blockIdx]}</span>
              <span className="text-gray-500">⋮⋮</span>
            </div>
          ))}
        </div>

        {/* Submit button */}
        <button
          onClick={handleSubmit}
          className="w-full mb-4 px-6 py-3 bg-emerald-neon text-gray-900 font-bold rounded-lg hover:bg-emerald-400"
        >
          Sprawdz kolejnosc
        </button>

        {/* Feedback */}
        {feedback && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 text-red-300 rounded-lg">
            {feedback}
          </div>
        )}

        {/* Hint */}
        {hint && (
          <div className="mb-4 p-3 bg-blue-900/50 border border-blue-500 text-blue-300 rounded-lg">
            <strong>Podpowiedz:</strong> {hint}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center">
          <button
            onClick={requestHint}
            disabled={hintsUsed >= 3}
            className="px-4 py-2 text-gray-400 hover:text-yellow-400 disabled:opacity-50"
          >
            Popros o podpowiedz ({hintsUsed}/3)
            {hintsUsed > 0 && <span className="text-xs ml-1">(-{hintsUsed === 1 ? 0 : hintsUsed === 2 ? 50 : 150} pkt)</span>}
          </button>

          <div className="text-gray-500 text-sm">
            Nagroda: {puzzle.difficulty === 'easy' ? 150 : puzzle.difficulty === 'medium' ? 250 : 500} pkt
          </div>
        </div>
      </div>
    </div>
  )
}
